import { useCallback, useEffect, useMemo, useState } from 'react';
import { ModalBody, ModalDialog, ModalHeader, ModalOverlay } from '@flos/react-ui';
import { useStateValue } from '../../providers/StateProvider';
import { useConfigContext } from '../../context/ConfigContext';
import { resetError } from '../../helpers/shared';
import { useFlowContext } from '../../context/FlowContext';
import { BLOCK_TYPES } from '../../interfaces/interfaces';
import { RMO } from '../../components/RMO';
import { ContactUs, AlreadyCovered, BornholmRedirect } from '../../components/MessageBoxContent';
import track from 'c9-tracking';
import { useUserContext } from '../../context/UserContext';
import { setSelectionValue } from '../../store/Actions';

enum IModalState {
    RMO = 'RMO',
    CONTACT_US = 'CONTACT_US',
    REDIRECT_BORNHOLM = 'REDIRECT_BORNHOLM',
    REDIRECT_MIT = 'REDIRECT_MIT',
    DFIM = 'DFIM',
    ERROR = 'ERROR'
}

const modalContent: Record<IModalState, JSX.Element> = {
    RMO: <RMO />,
    DFIM: <RMO />,
    CONTACT_US: <ContactUs />,
    REDIRECT_BORNHOLM: <BornholmRedirect />,
    REDIRECT_MIT: <AlreadyCovered />,
    ERROR: <></>
};

export const FlowBlockedModal = () => {
    const [{ error, selection, labels }, dispatch] = useStateValue();
    const config = useConfigContext();
    const user = useUserContext();
    const flow = useFlowContext();
    const [show, setShow] = useState<boolean>(false);
    const [modalState, setModalState] = useState<IModalState | undefined>();

    const body: React.ReactElement = useMemo(
        () => (!!modalState ? modalContent[modalState] : modalContent.ERROR),
        [modalState]
    );

    useEffect(() => {
        setShow(!!flow.blockType);
    }, [flow.blockType]);

    useEffect(() => {
        // this is a quick hack. if condition is true, state has been reset with dispatch({ type: RESET, payload: {} });
        // so remove the modal
        if (Object.getOwnPropertyNames(labels).length === 0) {
            setShow(false);
        }
    }, [labels]);

    const trackViolatedBusinessRule = (message: string, blockType: string) => {
        track.info([
            { key: message, value: blockType },
            { key: 'business-rule', value: true }
        ]);
    };

    useEffect(() => {
        if (flow.blockType) {
            switch (flow.blockType) {
                //Handled in default block
                // case BLOCK_TYPES.CAR_NOT_IN_LIST:
                // case BLOCK_TYPES.CAR_TOO_OLD:
                // case BLOCK_TYPES.CUSTOMER_BELOW_18:
                // case BLOCK_TYPES.TOO_MANY_CLAIMS:
                // case BLOCK_TYPES.CAR_DATA_DEFICIENT:
                // case BLOCK_TYPES.GENERAL_ERROR:
                // case BLOCK_TYPES.UNSUPPORTED_ADDRESS_BBR:
                // case BLOCK_TYPES.CAR_NO_CONTENT_37_38:

                case BLOCK_TYPES.D_STATUS_EARLY:
                case BLOCK_TYPES.D_STATUS_LATE:
                    trackViolatedBusinessRule('show-rmo-modal', flow.blockType);
                    setModalState(IModalState.DFIM);
                    break;
                case BLOCK_TYPES.CUSTOMER_MARKING:
                    trackViolatedBusinessRule('show-contact-us-modal', flow.blockType);
                    setModalState(IModalState.CONTACT_US);
                    break;
                case BLOCK_TYPES.CAR_ALREADY_COVERED:
                    if (user.loggedIn) {
                        trackViolatedBusinessRule('show-redirection-modal', flow.blockType);
                        setModalState(IModalState.REDIRECT_MIT);
                    } else {
                        trackViolatedBusinessRule('show-rmo-modal', flow.blockType);
                        setModalState(IModalState.RMO);
                    }
                    break;
                case BLOCK_TYPES.UNSUPPORTED_ADDRESS_BORNHOLM:
                    trackViolatedBusinessRule('show-redirection-modal', flow.blockType);
                    setModalState(IModalState.REDIRECT_BORNHOLM);
                    break;
                default:
                    trackViolatedBusinessRule('show-rmo-modal', flow.blockType);
                    setModalState(IModalState.RMO);
                    break;
            }
        }
    }, [flow.blockType, user.loggedIn]);

    const handleDismiss = useCallback(async () => {
        const origin = window.location.origin;
        const dismissTargetURL = {
            CHILD: `${origin}/forsikringer/boerneforsikring/`,
            CAR: `${origin}/forsikringer/bilforsikring/`
        };

        setShow(false);
        if (error.hasError) {
            resetError(dispatch);

            // if the error object has a redirectToPage, redirect to it when modal is closed
            if (error.redirectToPage) {
                const originalHost = window.topContext.config.getSiteConfig().originalHost;
                const targetUrl = `${window.location.protocol}//${originalHost}${config.appBasePath}${error.redirectToPage}`;
                window.location.replace(targetUrl);
            }
        }
        switch (flow.blockType) {
            case BLOCK_TYPES.CUSTOMER_BELOW_18:
            case BLOCK_TYPES.UNSUPPORTED_ADDRESS_BORNHOLM:
            case BLOCK_TYPES.CUSTOMER_MARKING:
            case BLOCK_TYPES.GENERAL_ERROR:
            case BLOCK_TYPES.CAR_ALREADY_COVERED:
            case BLOCK_TYPES.CAR_NOT_IN_LIST:
            case BLOCK_TYPES.WILL_NOT_INFORM_CPR:
                window.location.replace(dismissTargetURL[config.product]);
                break;
            case BLOCK_TYPES.CAR_OTHER_NEEDS:
                setSelectionValue(dispatch, selection, { key: 'isNeedOtherCoverage', value: false }, true);
                break;
            case BLOCK_TYPES.CAR_NO_CONTENT_37_38:
                flow.setFlowBlocked(false);
                window.location.replace(dismissTargetURL[config.product]);
                break;
        }
    }, [error.hasError, error.redirectToPage, dispatch, config.appBasePath, config.product, selection, flow]);

    return (
        <ModalOverlay isOpen={show} onDismiss={() => handleDismiss()} id="test-modal">
            <ModalDialog closeIconHandler={() => handleDismiss()} position="center">
                <ModalHeader></ModalHeader>
                <ModalBody>
                    <div className="u-spacing-inset-sm">{body}</div>
                </ModalBody>
            </ModalDialog>
        </ModalOverlay>
    );
};
